'use client';

import { motion, AnimatePresence, useMotionValue, useTransform, animate, type PanInfo } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { languages } from '@/data/languages';
import { t, tFromTo, countryNameIn } from '@/data/translations';

export type StatementDirection = 'to_america' | 'to_world' | 'america_to_america';

export interface StatementCard {
  id: string;
  text_original: string;
  text_english: string | null;
  text_translations?: Record<string, string> | null;
  language_code: string;
  country_code: string;
  country_name: string;
  direction: StatementDirection;
  likes: number;
  super_likes: number;
  flag: string;
  is_seed?: boolean;
  is_mine?: boolean;
}

// Pick the best text to show given the user's UI language.
// Priority: original (if user's lang matches) → pre-translated → English → original fallback.
export function pickText(card: StatementCard, langCode: string): string {
  if (card.language_code === langCode) return card.text_original;
  const t = card.text_translations?.[langCode];
  if (t) return t;
  return card.text_english ?? card.text_original;
}

interface SwipeCardProps {
  card: StatementCard;
  onVote: (type: 'like' | 'dislike' | 'super_like') => void;
  onWriteOwn: () => void;
  exitDirection: 'left' | 'right' | 'up' | null;
  showingOriginal: boolean;
  onToggleSide: () => void;
  langCode: string;
}

function audienceFor(direction: StatementDirection, langCode: string): string {
  return direction === 'to_world' ? t('world', langCode) : t('america', langCode);
}

function CardFace({
  card,
  text,
  otherLangLabel,
  onToggle,
  onVote,
  onFullscreen,
  isBack,
  langCode,
}: {
  card: StatementCard;
  text: string;
  otherLangLabel: string | null;
  onToggle: () => void;
  onVote: (type: 'like' | 'dislike' | 'super_like') => void;
  onFullscreen: () => void;
  isBack: boolean;
  langCode: string;
}) {
  const headerLine = tFromTo(
    langCode,
    countryNameIn(card.country_code, langCode, true),
    audienceFor(card.direction, langCode)
  );

  return (
    <div
      className={`${isBack ? 'absolute inset-0' : 'relative'} bg-gradient-to-br from-slate-800 via-slate-850 to-slate-900 rounded-3xl shadow-2xl border border-slate-700/50 overflow-hidden`}
      style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: isBack ? 'rotateY(180deg)' : undefined,
      }}
    >
      {/* Country header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
        <div className="flex items-start gap-2 min-w-0">
          <span className="text-2xl shrink-0 leading-none">{card.flag}</span>
          <span className="text-slate-300 font-medium text-sm leading-snug pt-1" lang={langCode}>
            {headerLine}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {otherLangLabel && (
            <button
              onClick={onToggle}
              className="text-xs text-slate-400 bg-slate-800 border border-slate-700 rounded-full px-3 py-1 active:bg-slate-700 hover:bg-slate-700 transition-colors"
            >
              {otherLangLabel}
            </button>
          )}
          <button
            onClick={onFullscreen}
            aria-label="Expand to fullscreen"
            className="w-7 h-7 flex items-center justify-center rounded-full text-slate-400 bg-slate-800 border border-slate-700 active:bg-slate-700 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Statement text */}
      <div className="px-5 pb-4 min-h-[260px] sm:min-h-[220px] max-h-[44vh] overflow-y-auto flex items-center">
        <p className="text-xl md:text-2xl leading-relaxed text-white">
          {text}
        </p>
      </div>

      {/* Score */}
      <div className="px-5 pb-3 flex items-center gap-4 text-sm text-slate-600">
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
          </svg>
          {card.likes + card.super_likes}
        </span>
      </div>

      {/* Vote buttons */}
      <div className="flex items-center justify-center gap-3 px-5 pb-5 pt-1 border-t border-slate-700/30">
        <button
          onClick={() => onVote('dislike')}
          aria-label="Dislike"
          className="flex-1 flex items-center justify-center py-3 rounded-2xl bg-slate-800/80 border border-slate-700/50 text-slate-400 active:scale-95 active:bg-red-500/20 active:border-red-500/50 active:text-red-400 transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
          </svg>
        </button>

        <button
          onClick={() => onVote('super_like')}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/10 border-2 border-amber-400/30 text-amber-400 active:scale-95 active:bg-amber-400/30 active:border-amber-400 transition-all"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </button>

        <button
          onClick={() => onVote('like')}
          aria-label="Like"
          className="flex-1 flex items-center justify-center py-3 rounded-2xl bg-slate-800/80 border border-slate-700/50 text-slate-400 active:scale-95 active:bg-emerald-500/20 active:border-emerald-500/50 active:text-emerald-400 transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.904 0 .715-.211 1.413-.608 2.008L7 11V21m7-10h-2M7 21H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function SwipeCard({ card, onVote, exitDirection, showingOriginal, onToggleSide, langCode }: SwipeCardProps) {
  // Translation availability: any non-original language version we can show on the back face.
  const userLangTranslation = card.language_code !== langCode ? card.text_translations?.[langCode] : null;
  const hasTranslation = card.language_code !== langCode && (Boolean(userLangTranslation) || (card.text_english !== null && card.language_code !== 'en'));
  const translationText = userLangTranslation ?? card.text_english ?? card.text_original;
  // Language code we're rendering the back face in (for `lang=` font shaping).
  const translationLang = userLangTranslation ? langCode : 'en';

  const [fullscreen, setFullscreen] = useState(false);
  const [committed, setCommitted] = useState(false);

  // Drag motion values
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 0, 300], [-15, 0, 15]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const nopeOpacity = useTransform(x, [-140, -40], [1, 0]);
  const superOpacity = useTransform(y, [-140, -40], [1, 0]);

  const handleDragEnd = useCallback(
    (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (committed) return;
      const SWIPE_DIST = 100;
      const SWIPE_VEL = 500;
      const { offset, velocity } = info;

      // Prefer the dominant axis to avoid ambiguous diagonal swipes
      const horizontal = Math.abs(offset.x) > Math.abs(offset.y);

      if (horizontal && (offset.x < -SWIPE_DIST || velocity.x < -SWIPE_VEL)) {
        setCommitted(true);
        animate(x, -600, { duration: 0.3, ease: 'easeOut' });
        onVote('dislike');
      } else if (horizontal && (offset.x > SWIPE_DIST || velocity.x > SWIPE_VEL)) {
        setCommitted(true);
        animate(x, 600, { duration: 0.3, ease: 'easeOut' });
        onVote('like');
      } else if (!horizontal && (offset.y < -SWIPE_DIST || velocity.y < -SWIPE_VEL)) {
        setCommitted(true);
        animate(y, -700, { duration: 0.3, ease: 'easeOut' });
        onVote('super_like');
      } else {
        animate(x, 0, { type: 'spring', stiffness: 400, damping: 35 });
        animate(y, 0, { type: 'spring', stiffness: 400, damping: 35 });
      }
    },
    [committed, onVote, x, y]
  );

  const handleToggle = useCallback(() => {
    if (hasTranslation) {
      onToggleSide();
    }
  }, [hasTranslation, onToggleSide]);

  // Language label lookup — show the native name for each language on the toggle button.
  const originalLangInfo = useMemo(
    () => languages.find(l => l.code === card.language_code),
    [card.language_code]
  );
  const translationLangInfo = useMemo(
    () => languages.find(l => l.code === translationLang),
    [translationLang]
  );
  const originalLangLabel = originalLangInfo?.name || card.language_code.toUpperCase();
  const translationLangLabel = translationLangInfo?.name || translationLang.toUpperCase();

  const visibleText = showingOriginal ? card.text_original : translationText;
  const fullscreenHeader = tFromTo(
    langCode,
    countryNameIn(card.country_code, langCode, true),
    audienceFor(card.direction, langCode)
  );

  // Exit animation based on vote direction
  const exitVariants = {
    left: { x: -500, opacity: 0, rotate: -25, transition: { duration: 0.45, ease: 'easeIn' as const } },
    right: { x: 500, opacity: 0, rotate: 25, transition: { duration: 0.45, ease: 'easeIn' as const } },
    up: { y: -600, opacity: 0, scale: 0.85, transition: { duration: 0.45, ease: 'easeIn' as const } },
  };

  return (
    <>
      <motion.div
        className="absolute inset-0 flex items-center justify-center px-4"
        initial={{ scale: 0.9, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={exitDirection ? exitVariants[exitDirection] : { opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{ zIndex: 10 }}
      >
        <motion.div
          className="w-full max-w-sm md:max-w-lg lg:max-w-xl relative"
          style={{ perspective: 1400, x, y, rotate, touchAction: 'none' }}
          drag={!committed && !exitDirection}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
        >
          {/* 3D flip container */}
          <motion.div
            className="relative"
            animate={{ rotateY: showingOriginal ? 0 : 180 }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* FRONT: Original language. Toggle takes user to the translation in their UI language. */}
            <CardFace
              card={card}
              text={card.text_original}
              otherLangLabel={hasTranslation ? translationLangLabel : null}
              onToggle={handleToggle}
              onVote={onVote}
              onFullscreen={() => setFullscreen(true)}
              isBack={false}
              langCode={langCode}
            />

            {/* BACK: Translation in the user's UI language (falls back to English). */}
            {hasTranslation && (
              <CardFace
                card={card}
                text={translationText}
                otherLangLabel={originalLangLabel}
                onToggle={handleToggle}
                onVote={onVote}
                onFullscreen={() => setFullscreen(true)}
                isBack={true}
                langCode={translationLang}
              />
            )}
          </motion.div>

          {/* Drag direction indicators */}
          <motion.div
            className="pointer-events-none absolute top-8 left-6 px-3 py-1.5 rounded-lg border-2 border-red-500 text-red-500 font-bold text-sm tracking-wider rotate-[-12deg]"
            style={{ opacity: nopeOpacity }}
          >
            NAH
          </motion.div>
          <motion.div
            className="pointer-events-none absolute top-8 right-6 px-3 py-1.5 rounded-lg border-2 border-emerald-400 text-emerald-400 font-bold text-sm tracking-wider rotate-[12deg]"
            style={{ opacity: likeOpacity }}
          >
            YES!
          </motion.div>
          <motion.div
            className="pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg border-2 border-amber-400 text-amber-400 font-bold text-sm tracking-wider"
            style={{ opacity: superOpacity }}
          >
            STAR
          </motion.div>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {fullscreen && (
          <motion.div
            className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={() => setFullscreen(false)}
              aria-label="Close fullscreen"
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="min-h-full flex items-center justify-center px-6 py-20">
              <div className="max-w-3xl w-full">
                <div className="flex items-center gap-3 mb-8">
                  <span className="text-3xl">{card.flag}</span>
                  <span className="text-slate-400 text-base" lang={langCode}>
                    {fullscreenHeader}
                  </span>
                </div>
                <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-white whitespace-pre-wrap break-words">
                  {visibleText}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
